(() => {
  const gateContext = document.currentScript?.dataset;
  const membershipUrl = 'https://adaptiveambition.com/products/adaptive-ambition-professional-membership';
  const protectedPaths = new Set([
    '/pages/provider-hub',
    '/pages/resource-library',
    '/pages/complete-resource-index',
    '/pages/getting-started',
    '/pages/updates-versions',
    '/pages/energy-aware-planning',
    '/pages/brain-fog-executive-function',
    '/pages/patient-facing-everyday-support',
    '/pages/manage-provider-access',
  ]);
  const appstleDeniedText = 'This page is available to Adaptive Ambition Professional members.';
  const purchaseInstructions =
    'Professional resources are available to Adaptive Ambition Professional members. Already a member? Log in to your customer account, then return to this page. Not yet a member? Purchase a Professional Membership to gain access to the complete Professional Resource Library.';
  const licenseOptions = {
    'solo-professional': 'Solo Professional — 1 Provider',
    practice: 'Practice — Up to 5 Providers',
    'expanded-practice': 'Expanded Practice — Up to 15 Providers',
  };

  const preselectMembershipLicense = async () => {
    if (window.location.pathname !== '/products/adaptive-ambition-professional-membership') return;

    const requestedLicense = licenseOptions[new URLSearchParams(window.location.search).get('license')];
    if (!requestedLicense) return;

    await customElements.whenDefined('variant-picker');
    const licenseFieldset = Array.from(document.querySelectorAll('variant-picker fieldset')).find(
      (fieldset) => fieldset.querySelector('legend')?.textContent.trim() === 'License'
    );
    const licenseInput = Array.from(licenseFieldset?.querySelectorAll('input[type="radio"]') || []).find(
      (input) => input.value === requestedLicense
    );

    if (licenseInput && !licenseInput.checked) {
      licenseInput.checked = true;
      licenseInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const updateDraftGateCopy = () => {
    const loggedIn = gateContext.customerLoggedIn === 'true';
    const tags = JSON.parse(gateContext.customerTags || 'null') || [];
    const secondaryProvider = loggedIn && tags.some((tag) =>
      ['aa-pro-practice', 'aa-pro-expanded'].includes(tag.toLowerCase())
    );
    // Copy selection only; Appstle still decides whether access is denied.
    const professionalTags = new Set([
      'aa-pro-solo', 'aa-pro-solo-annual',
      'aa-pro-practice-monthly', 'aa-pro-practice-annual',
      'aa-pro-expanded-monthly', 'aa-pro-expanded-annual',
      'aa-pro-practice', 'aa-pro-expanded',
    ]);
    const hasProfessionalTag = tags.some((tag) => professionalTags.has(tag.toLowerCase()));
    // Appstle normally replaces main itself. Also support a blocker inside main.
    const gates = document.querySelectorAll('.appstle_membership_blocker_wrapper, #MainContent');
    for (const gate of gates) {
      if (!gate.textContent.includes(appstleDeniedText)) continue;
      if (gate.id === 'MainContent' && gate.querySelector('.appstle_membership_blocker_wrapper')) continue;
      const walker = document.createTreeWalker(gate, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          return node.parentElement.closest('a, button, script, style')
            ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        },
      });
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      if (secondaryProvider && window.location.pathname === '/pages/manage-provider-access') {
        // Change only an existing denial, never the authorized page or its controls.
        const messageNodes = textNodes.filter((node) => node.textContent.trim());
        if (!messageNodes.length) continue;
        messageNodes[0].textContent = 'This page is available to the primary membership holder only. If you’re an additional provider on a Practice or Expanded Practice membership, you can access the Professional Resource Library from the Provider Hub.';
        messageNodes.slice(1).forEach((node) => { node.textContent = ''; });
        for (const link of gate.querySelectorAll('a[href]')) {
          if (link.textContent.trim() !== 'Get Professional Membership') continue;
          link.setAttribute('href', '/pages/energy-aware-planning?view=providers');
          (link.querySelector('button') || link).textContent = 'Go to Provider Hub';
        }
      } else if (loggedIn && !hasProfessionalTag) {
        const messageNodes = textNodes.filter((node) => node.textContent.trim());
        if (!messageNodes.length) continue;
        messageNodes[0].textContent = 'This account does not currently have access to Adaptive Ambition Professional. If you believe you should have access through a Practice or Expanded Practice membership, please contact your membership administrator.';
        messageNodes.slice(1).forEach((node) => { node.textContent = ''; });
      } else if (!loggedIn) {
        for (const node of textNodes) {
          const match = /log in to your account/i.exec(node.textContent);
          if (!match) continue;
          const link = document.createElement('a');
          link.href = gateContext.customerLoginUrl;
          link.textContent = match[0];
          link.style.color = 'inherit';
          link.style.textDecoration = 'underline';
          const after = node.splitText(match.index);
          after.deleteData(0, match[0].length);
          after.before(link);
        }
      }
    }
  };

  const showDeniedInstructions = () => {
    if (!protectedPaths.has(window.location.pathname)) return;
    if (window.Shopify?.theme?.id === 166846169308 && gateContext?.professionalGateUx !== undefined) {
      updateDraftGateCopy();
      return;
    }

    const main = document.querySelector('#MainContent');
    if (!main || !main.textContent.includes(appstleDeniedText)) return;
    if (main.querySelector('[data-professional-access-denied]')) return;

    const notice = document.createElement('div');
    notice.dataset.professionalAccessDenied = '';
    notice.style.cssText =
      'max-width:48rem;margin:3rem auto;padding:0 1.5rem;text-align:center;font:inherit;line-height:1.6;';

    const message = document.createElement('p');
    message.textContent = purchaseInstructions;

    const link = document.createElement('a');
    link.href = membershipUrl;
    link.textContent = 'Purchase Professional Membership';
    link.style.cssText =
      'display:inline-flex;min-height:44px;align-items:center;justify-content:center;margin-top:1.25rem;padding:.75rem 1.5rem;border:1px solid #6f1d35;border-radius:var(--style-border-radius-buttons-primary, 0);background:#6f1d35;color:#fff;font-weight:600;text-align:center;text-decoration:none;';

    notice.append(message, link);
    main.replaceChildren(notice);
  };

  const standardizeProfessionalEyebrow = () => {
    if (!document.body.classList.contains('professional-experience')) return;

    const eyebrowText = 'ADAPTIVE AMBITION PROFESSIONAL';

    const mainHeading =
      document.querySelector('#MainContent h1') ||
      document.querySelector('#MainContent .text-block > :is(h2, h3, p):not(.professional-system__eyebrow)');
    if (!mainHeading) return;

    if (!mainHeading.classList.contains('professional-system__page-title')) {
      mainHeading.classList.add('professional-system__page-title');
    }

    const headingContainer = mainHeading.parentElement;
    let eyebrow = headingContainer?.querySelector(
      ':scope > .professional-system__eyebrow, :scope > [class$="__eyebrow"]'
    );

    if (!eyebrow) {
      eyebrow = document.createElement('p');
      mainHeading.before(eyebrow);
    }

    if (!eyebrow.classList.contains('professional-system__eyebrow')) {
      eyebrow.classList.add('professional-system__eyebrow');
    }
    if (eyebrow.textContent.trim() !== eyebrowText) {
      eyebrow.textContent = eyebrowText;
    }
  };

  const synchronizeProfessionalExperience = () => {
    standardizeProfessionalEyebrow();
    showDeniedInstructions();
  };

  synchronizeProfessionalExperience();
  preselectMembershipLicense();
  document.addEventListener('AppstleMembership:CustomerPortal:ReadyToEmbed', synchronizeProfessionalExperience);
  new MutationObserver(synchronizeProfessionalExperience).observe(document.body, { childList: true, subtree: true });
})();
